import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import type { DynamicInvoiceMeasurementChartData } from '@weft/api-definition';
import type { ProductLineItem } from '@weft/services';
import { isCommissionProduct, isCommitmentLineItem, isLineItemWithUnits, isRefundProduct } from '@weft/services';
import { DEFAULT_CURRENCY, INVOICE_REVIEW_STATUSES } from '@weft/types';
import { isNullOrUndefined } from '@weft/utils';
import React from 'react';
import { getProductTitle } from '../../../../../services/product/product-title';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import { calculateAndFormatPercentage, UsageTotalProgress } from '../UsageTotalProgress';
import { ModifyFixedLineItemSection } from './dynamic-invoice-fixed-line-items/ModifyFixedLineItemSection';
import { DynamicInvoiceProductLineItemDetails } from './dynamic-invoice-product-line-item-components/DynamicInvoiceProductLineItemDetails';
import { DynamicInvoiceProductLineItemUsageCollapseBody } from './dynamic-invoice-product-line-item-components/DynamicInvoiceProductLineItemUsageCollapseBody';
import { LeftBorder } from './dynamic-invoice-product-line-item-components/LeftBorder';
import { useCommitmentUnits } from './dynamic-invoice-product-line-item-hooks/use-commitment-units';
import { useOverchargedChecker } from './dynamic-invoice-product-line-item-hooks/use-overcharge-checker';

type DynamicInvoiceProductLineItemProps = {
  productLineItem: ProductLineItem;
  measurementChartData: DynamicInvoiceMeasurementChartData;
  invoiceId: string;
  customerId: string;
  isOnlyOneProductLineItem: boolean;
};

export const DynamicInvoiceProductLineItem: React.FC<DynamicInvoiceProductLineItemProps> = ({ productLineItem: lineItem, customerId, measurementChartData, invoiceId, isOnlyOneProductLineItem }) => {
  const { lineItemProduct } = lineItem;
  const isNotFixedLineItem = isLineItemWithUnits(lineItem);
  const hasCommitment = isCommitmentLineItem(lineItem);
  const isCommission = isCommissionProduct(lineItemProduct);
  const commitmentUnits = useCommitmentUnits(lineItem);
  const isOvercharged = useOverchargedChecker(lineItem);
  const { dynamicInvoiceData } = useDynamicInvoice();
  const isEditDisabled = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const shouldShowUsage = isNotFixedLineItem && hasCommitment;
  const commitment = lineItem.lineItemProduct.commitment;
  const total = commitment?.consumption ?? 0;
  const max = commitment?.amount ?? 0;
  const { percentage } = calculateAndFormatPercentage(total, max);

  return (
    <>
      <Accordion >
        <AccordionSummary expandIcon={isNotFixedLineItem && <ExpandMoreIcon />}>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            
            { isOvercharged && <LeftBorder /> }
            
            <Box sx={{ width: '250px', paddingY: 4, flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip
                  title={lineItem.modifiedName ?? getProductTitle(lineItemProduct)}
                  arrow
                >
                  <Typography
                    variant='subtitle1'
                    fontWeight={400}
                    fontSize="16px"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {lineItem.modifiedName ?? getProductTitle(lineItemProduct)}
                  </Typography>
                </Tooltip>
                
                {isRefundProduct(lineItemProduct) && (
                  <Chip
                    label="Refund"
                    size='small'
                    sx={{
                      fontSize: '0.875rem', 
                      padding: '4px',
                      height: 'auto', 
                      marginRight: '20px',
                    }}
                  />
                )}
                { shouldShowUsage && percentage > 100 && (
                  <Chip
                    label="Overcharges"
                    size='small'
                    sx={{
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      color: '#C62828',
                      fontSize: '0.875rem', 
                      padding: '4px',
                      height: 'auto', 
                      marginRight: '20px',
                    }}
                  />
                )}
              </Box>
              <Tooltip
                title={lineItem.modifiedDescription}
                arrow
              >
                <Typography
                  variant="body2"
                  color='rgba(27, 61, 85, 0.60)'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {lineItem.modifiedDescription}
                </Typography>
              </Tooltip>
            </Box>
            
            {shouldShowUsage && !isOvercharged && (
              <div>
                <UsageTotalProgress
                  total={total}
                  max={max}
                />
              </div>
            )}
            
            <DynamicInvoiceProductLineItemDetails 
              isCommission= {isCommission}
              commitmentUnits= {commitmentUnits}
              isNotFixedLineItem= {isNotFixedLineItem}
              pricingConfiguration={isCommission ? lineItemProduct.variants[0].pricing.priceConfiguration : undefined}  
              lineItemPrice= {isNullOrUndefined(lineItem.revisionedLineItem) ? lineItem.price : lineItem.revisionedLineItem.price}
              currency= {dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY}
              lineItemProduct= {lineItem.lineItemProduct}
            />
            
            {(!isNotFixedLineItem && !isEditDisabled && !isCommission) && (
              <ModifyFixedLineItemSection
                customerId={customerId}
                invoiceId={invoiceId}
                lineItem={lineItem}
              />
            )}
          </Box>
        </AccordionSummary>
        
        {isNotFixedLineItem && (
          <AccordionDetails>
            <DynamicInvoiceProductLineItemUsageCollapseBody
              lineItem={lineItem}
              customerId={customerId}
              measurementChartData={measurementChartData}
            />
          </AccordionDetails>
        )}

      </Accordion>
      {!isOnlyOneProductLineItem && <Divider/>}
    </>
  );
};